/**
 * Cart Drawer Module
 * Handles drawer controls, recommendations, and upsell slider
 */
(function() {
  'use strict';

  // Recommendations cache (5 min TTL, max 10 entries)
  const cache = new Map();
  const CACHE_TTL = 5 * 60 * 1000;
  const CACHE_MAX_SIZE = 10;

  let isLoading = false;
  let lastProductIds = '';
  let debounceTimer = null;

  // Cached DOM references
  let drawer, container, sliderWrapper, prevBtn, nextBtn;

  function init() {
    drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    initDrawerControls();
    initRecommendations();
    initUpsellSlider();
  }

  function initDrawerControls() {
    const cartProducts = drawer.querySelector('[data-cart-products]');

    function closeDrawer() {
      drawer.setAttribute('aria-expanded', 'false');
      if (typeof drawer.closeCart === 'function') drawer.closeCart();
      else if ('expanded' in drawer) drawer.expanded = false;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    function updateEmptyState() {
      const hasItems = cartProducts?.querySelector('[data-quantity-selector], [data-free-gift-item]');
      drawer.setAttribute('data-cart-empty', hasItems ? 'false' : 'true');
    }

    // Observe cart products changes
    if (cartProducts) {
      new MutationObserver(updateEmptyState).observe(cartProducts, { childList: true });
    }

    // Lock page scroll while open (no body class: toggling it restyled the whole page, INP)
    new MutationObserver(() => {
      const isOpen = drawer.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['aria-expanded'] });

    // Event listener for cart updates
    document.addEventListener('cart:updated', updateEmptyState);

    // Close on overlay/button click
    document.addEventListener('click', (e) => {
      if (drawer.getAttribute('aria-expanded') !== 'true') return;
      if (e.target.closest('[data-close-button]') || e.target.matches('[data-cart-overlay]')) {
        e.preventDefault();
        e.stopPropagation();
        closeDrawer();
      }
    }, true);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-expanded') === 'true') closeDrawer();
    });

    // Expose for external use
    drawer.openDrawer = () => {
      drawer.setAttribute('aria-expanded', 'true');
    };
  }

  function initRecommendations() {
    container = document.querySelector('[data-cart-upsells]');
    if (!container) return;

    // Handle single variant add to cart clicks (event delegation)
    document.addEventListener('click', handleAddToCart);

    // Listen for cart changes
    document.addEventListener('cart:updated', debouncedLoad);
    document.addEventListener('theme:cart:change', debouncedLoad);

    // Load as soon as the drawer starts opening (not transitionend) to hide fetch latency
    new MutationObserver(() => {
      if (drawer.getAttribute('aria-expanded') === 'true') loadRecommendations();
    }).observe(drawer, { attributes: true, attributeFilter: ['aria-expanded'] });

    // Warm the cache while the page is idle so the first open renders instantly
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => loadRecommendations(), { timeout: 4000 });
    } else {
      setTimeout(loadRecommendations, 2500);
    }
  }

  function handleAddToCart(e) {
    const button = e.target.closest('[data-single-variant-add-to-cart]');
    if (!button) return;

    e.preventDefault();
    e.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');

    const sectionId = drawer.dataset.sectionId;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: 1 }],
        sections: sectionId ? [sectionId] : []
      })
    })
    .then(r => r.json())
    .then((response) => {
      if (typeof drawer.update === 'function') {
        drawer.update(response);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      button.setAttribute('aria-busy', 'false');
    })
    .catch(() => {
      button.setAttribute('aria-busy', 'false');
    });
  }

  function debouncedLoad() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      lastProductIds = '';
      loadRecommendations();
    }, 300);
  }

  async function loadRecommendations() {
    if (isLoading) return;
    // Re-query in case a section re-render replaced the node
    container = document.querySelector('[data-cart-upsells]') || container;
    if (!container) return;
    isLoading = true;

    try {
      const cart = await fetch('/cart.js').then(r => { if (!r.ok) throw new Error(`Cart fetch failed: ${r.status}`); return r.json(); });
      if (!cart.items.length) {
        container.innerHTML = '';
        return;
      }

      const productIds = cart.items.map(i => i.product_id);
      const key = productIds.join(',');

      if (key === lastProductIds && container.children.length) return;
      lastProductIds = key;

      // Check cache
      const cached = cache.get(key);
      if (cached && Date.now() - cached.ts < CACHE_TTL) {
        renderRecommendations(cached.data, productIds);
        return;
      }

      // Fetch recommendations (limit to first 2 products)
      const responses = await Promise.all(
        productIds.slice(0, 2).map(id =>
          fetch(`/recommendations/products?product_id=${id}&limit=4&section_id=api-cart-recommendations`)
            .then(r => r.text()).catch(() => '')
        )
      );

      // Maintain cache size
      if (cache.size >= CACHE_MAX_SIZE) {
        const oldestKey = cache.keys().next().value;
        cache.delete(oldestKey);
      }
      cache.set(key, { data: responses, ts: Date.now() });

      renderRecommendations(responses, productIds);

    } catch (e) {
      console.error('Recommendations error:', e);
    } finally {
      isLoading = false;
    }
  }

  function renderRecommendations(responses, productIds) {
    const seen = new Set(productIds);
    const fragment = document.createDocumentFragment();

    for (const html of responses) {
      if (!html) continue;
      const template = document.createElement('template');
      template.innerHTML = html;

      for (const el of template.content.querySelectorAll('.cart-recommendation-item')) {
        if (fragment.childElementCount >= 6) break;
        const pid = parseInt(el.dataset.productId);
        if (!seen.has(pid)) {
          seen.add(pid);
          fragment.appendChild(el);
        }
      }
      if (fragment.childElementCount >= 6) break;
    }

    container.innerHTML = '';
    container.appendChild(fragment);
    updateSliderButtons();
  }

  function initUpsellSlider() {
    sliderWrapper = document.querySelector('.cart-upsell-wrapper');
    prevBtn = document.querySelector('.cart-upsell-prev');
    nextBtn = document.querySelector('.cart-upsell-next');

    if (!sliderWrapper) return;

    const scrollAmount = 272;

    if (prevBtn) prevBtn.addEventListener('click', () => sliderWrapper.scrollBy({ left: -scrollAmount, behavior: 'smooth' }));
    if (nextBtn) nextBtn.addEventListener('click', () => sliderWrapper.scrollBy({ left: scrollAmount, behavior: 'smooth' }));

    sliderWrapper.addEventListener('scroll', updateSliderButtons, { passive: true });

    // Desktop drag functionality
    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;

    sliderWrapper.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isDragging = true;
      sliderWrapper.classList.add('dragging');
      startX = e.clientX;
      scrollStart = sliderWrapper.scrollLeft;
      e.preventDefault();
    });

    document.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      sliderWrapper.classList.remove('dragging');
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      sliderWrapper.scrollLeft = scrollStart - (e.clientX - startX);
    });

    // Prevent link clicks during drag
    sliderWrapper.addEventListener('click', (e) => {
      if (Math.abs(sliderWrapper.scrollLeft - scrollStart) > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  function updateSliderButtons() {
    if (!sliderWrapper) return;
    if (prevBtn) prevBtn.disabled = sliderWrapper.scrollLeft <= 0;
    if (nextBtn) nextBtn.disabled = sliderWrapper.scrollLeft >= sliderWrapper.scrollWidth - sliderWrapper.clientWidth - 5;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
